/**
 * Stocktake list page — `/stock/stocktakes` (§8.1 + §8.2).
 *
 * Replaces the legacy `StocktakesPage` (which was a Card + AntD Table +
 * Modal-based detail). The new page is list-only; click a row to
 * navigate to `/stock/stocktakes/:id` (see `StocktakeDetailPage`).
 *
 * CTA: "+ New stocktake". Disabled with a tooltip when any in-progress
 * stocktake exists (`GET /api/stocktakes?finalized=false&limit=1`). The
 * disabled-by-state pre-empts the click; the backend still 409s on a
 * race, but the UX is much smoother this way.
 *
 * Sort: started_at desc by default. Columns are individually sortable
 * via AntD's column sorters.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import { useNavigate } from "react-router-dom";

import { ApiError, type StocktakeRow, api } from "../../api/client";
import { formatCompactDateTime } from "../../utils/dateFormat";

const { Title, Text } = Typography;

export const StocktakeListPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<StocktakeRow[]>([]);
  const [openInProgress, setOpenInProgress] = useState<StocktakeRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [all, open] = await Promise.all([
        api.listStocktakes({ limit: 200 }),
        api.listStocktakes({ finalized: false, limit: 1 }),
      ]);
      setRows(all);
      setOpenInProgress(open[0] ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onNew = async () => {
    if (openInProgress) return;
    setCreating(true);
    setError(null);
    try {
      const created = await api.createStocktake({});
      navigate(`/stock/stocktakes/${created.id}`);
    } catch (err) {
      // 409 stocktake_in_progress is the race window we may have lost:
      // refetch + show a refreshed view.
      if (err instanceof ApiError) setError(err.message);
      else setError(String(err));
      await refresh();
    } finally {
      setCreating(false);
    }
  };

  const newCtaDisabled = openInProgress !== null || creating;
  const newCtaTooltip = openInProgress
    ? `Stocktake #${openInProgress.id} is in progress — wait for it to finalize.`
    : null;

  const columns: ColumnsType<StocktakeRow> = useMemo(
    () => [
      {
        title: "ID",
        dataIndex: "id",
        width: 70,
        sorter: (a, b) => a.id - b.id,
      },
      {
        title: "Started",
        dataIndex: "started_at",
        width: 140,
        render: formatCompactDateTime,
        sorter: (a, b) =>
          (a.started_at ?? "").localeCompare(b.started_at ?? ""),
        defaultSortOrder: "descend",
      } as ColumnType<StocktakeRow>,
      {
        title: "Counted at",
        dataIndex: "counted_at",
        width: 140,
        render: formatCompactDateTime,
        sorter: (a, b) =>
          (a.counted_at ?? "").localeCompare(b.counted_at ?? ""),
      },
      {
        title: "Finalized",
        dataIndex: "finalized_at",
        width: 160,
        render: (v: string | null) =>
          v ? (
            formatCompactDateTime(v)
          ) : (
            <Tag color="orange">in-progress</Tag>
          ),
        sorter: (a, b) =>
          (a.finalized_at ?? "").localeCompare(b.finalized_at ?? ""),
      },
      {
        title: "Lines",
        dataIndex: "line_count",
        align: "right",
        width: 80,
        sorter: (a, b) => a.line_count - b.line_count,
      },
      {
        title: "Operator",
        dataIndex: "operator_id",
        width: 120,
        render: (id: number | null) =>
          id === null ? <Text type="secondary">—</Text> : `#${id}`,
        sorter: (a, b) => (a.operator_id ?? 0) - (b.operator_id ?? 0),
      },
      {
        title: "Comment",
        dataIndex: "comment",
        ellipsis: true,
        render: (v: string | null) =>
          v ? v : <Text type="secondary">—</Text>,
      },
    ],
    [],
  );

  return (
    <Card>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Stocktakes
        </Title>
        <Tooltip title={newCtaTooltip ?? ""} placement="bottomRight">
          <Button
            type="primary"
            onClick={() => void onNew()}
            disabled={newCtaDisabled}
            loading={creating}
          >
            + New stocktake
          </Button>
        </Tooltip>
      </Space>

      {openInProgress && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <Space wrap>
              <span>
                Stocktake #{openInProgress.id} is in progress (started{" "}
                {formatCompactDateTime(openInProgress.started_at)}).
              </span>
              <Button
                size="small"
                type="primary"
                onClick={() =>
                  navigate(`/stock/stocktakes/${openInProgress.id}`)
                }
              >
                Open
              </Button>
            </Space>
          }
        />
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<StocktakeRow>
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25, showSizeChanger: false }}
        onRow={(row) => ({
          onClick: () => navigate(`/stock/stocktakes/${row.id}`),
          style: { cursor: "pointer" },
        })}
      />
    </Card>
  );
};

export default StocktakeListPage;
